// src/core/composables/createApiComposable.ts
import { ref, type Ref } from "vue";

type ToastFn = (title: string, message: string) => void;

export type ApiComposableOptions = {
  successToast?: ToastFn;
  errorToast?: ToastFn;
  defaultErrorMessage?: string;
};

function normalizeError(e: unknown, fallback: string) {
  const anyErr = e as any;
  return anyErr?.response?.data?.message ?? anyErr?.message ?? fallback;
}

export function createApiComposable(options: ApiComposableOptions = {}) {
  const loading = ref(false);
  const error = ref<string | null>(null);

  const clearError = () => (error.value = null);

  const run = async <T>(fn: () => Promise<T>, fallbackMsg?: string): Promise<T> => {
    loading.value = true;
    error.value = null;

    try {
      return await fn();
    } catch (e) {
      const msg = normalizeError(e, fallbackMsg ?? options.defaultErrorMessage ?? "Request failed");
      error.value = msg;
      options.errorToast?.("Error", msg);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const runAndAssign = async <T>(
    target: Ref<T>,
    fn: () => Promise<T>,
    fallbackMsg?: string
  ): Promise<T> => {
    const data = await run(fn, fallbackMsg);
    target.value = data;
    return data;
  };

  const runWithSuccess = async <T>(
    fn: () => Promise<T>,
    successTitle: string,
    successMessage: string,
    fallbackMsg?: string
  ): Promise<T> => {
    const data = await run(fn, fallbackMsg);
    options.successToast?.(successTitle, successMessage);
    return data;
  };

  const runVoid = async (fn: () => Promise<void>, fallbackMsg?: string): Promise<void> => {
    await run(fn, fallbackMsg);
  };

  const runVoidWithSuccess = async (
    fn: () => Promise<void>,
    successTitle: string,
    successMessage: string,
    fallbackMsg?: string
  ): Promise<void> => {
    await run(fn, fallbackMsg);
    options.successToast?.(successTitle, successMessage);
  };

  return {
    loading,
    error,
    clearError,
    run,
    runAndAssign,
    runWithSuccess,
    runVoid,
    runVoidWithSuccess,
  };
}
